import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import type { DeferredRequestKey } from '~/constants/common/deferredRequests';
import type { DeepPartial } from '~/types';

type DeferredFlags = Record<DeferredRequestKey, boolean>;

export type ContextType = {
  fellBackToClient: DeferredFlags;
  markFellBackToClient: (key: DeferredRequestKey) => void;
  // True when the server dispatched a deferred fetch, not that it resolved to a value:
  // a miss still reads true here and is recovered client-side via triggerFetchX.
  resolvedOnServer: DeferredFlags;
};

const noFlags: DeferredFlags = {
  deliveryPickup: false,
  discounts: false,
  favorites: false,
  ownerReviews: false,
  pricing: false,
  trades: false,
  whyBuy: false
};

export const defaultContext: ContextType = {
  fellBackToClient: noFlags,
  markFellBackToClient: () => {},
  resolvedOnServer: noFlags
};

const DeferredResolutionContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  resolvedOnServer?: ContextType['resolvedOnServer'];
}>;

export function DeferredResolutionContextProvider({
  children,
  resolvedOnServer
}: OwnProps) {
  const [fellBackToClient, setFellBackToClient] =
    useState<DeferredFlags>(noFlags);

  const markFellBackToClient = useCallback((key: DeferredRequestKey) => {
    setFellBackToClient(current =>
      current[key] ? current : { ...current, [key]: true }
    );
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      fellBackToClient,
      markFellBackToClient,
      resolvedOnServer: resolvedOnServer ?? defaultContext.resolvedOnServer
    }),
    [fellBackToClient, markFellBackToClient, resolvedOnServer]
  );

  return (
    <DeferredResolutionContext.Provider value={value}>
      {children}
    </DeferredResolutionContext.Provider>
  );
}

DeferredResolutionContextProvider.displayName =
  'DeferredResolutionContextProvider';

export const generateDeferredResolutionContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedDeferredResolutionContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <DeferredResolutionContext.Provider
      value={generateDeferredResolutionContext(mockValue)}
    >
      {children}
    </DeferredResolutionContext.Provider>
  );
}

export function useResolvedOnServer() {
  return useContext(DeferredResolutionContext).resolvedOnServer;
}

export function useFellBackToClient() {
  return useContext(DeferredResolutionContext).fellBackToClient;
}

// A ref, so reading it in an effect doesn't re-run the effect when a fallback lands.
export function useFellBackToClientRef() {
  const fellBackToClient = useFellBackToClient();
  const fellBackToClientRef = useRef(fellBackToClient);
  fellBackToClientRef.current = fellBackToClient;

  return fellBackToClientRef;
}

export function useMarkFellBackToClient() {
  return useContext(DeferredResolutionContext).markFellBackToClient;
}

export default DeferredResolutionContext;
